import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const Main = styled.section`
  padding: 110px 0 100px;
  position: relative;
  z-index: 10;
  background:
    radial-gradient(circle at 15% 10%, ${(props) => props.theme.text}10 0%, transparent 45%),
    linear-gradient(180deg, #fafaf7 0%, #f3f1ea 100%);

  @media screen and (max-width: 768px) {
    padding: 80px 0;
  }
`;

export const MaxWidth = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 50px;

  @media screen and (max-width: 768px) {
    padding: 0 22px;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

export const HeaderLeft = styled.div`
  max-width: 640px;
`;

export const Eyebrow = styled(motion.h2)`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${(props) => props.theme.text};
  margin-bottom: 10px;
`;

export const Heading = styled(motion.h1)`
  font-size: 44px;
  font-weight: 700;
  color: #0f1115;
  line-height: 1.1;
  margin: 0 0 14px;
  font-family: 'Poppins', sans-serif;

  span {
    font-style: italic;
    font-weight: 500;
    color: ${(props) => props.theme.text};
  }

  @media screen and (max-width: 768px) {
    font-size: 30px;
  }
`;

export const Lead = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a4a4a;
  margin: 0;
`;

export const Stats = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 22px;
  flex-wrap: wrap;
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    font-size: 28px;
    font-weight: 700;
    color: #0f1115;
    font-family: 'Poppins', sans-serif;
    line-height: 1;
  }

  span {
    font-size: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #6b6b6b;
  }
`;

export const ViewAll = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 999px;
  background: #0f1115;
  color: #fff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: transform 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateX(4px);
    background: ${(props) => props.theme.text};
  }
`;

export const Rail = styled.div`
  display: flex;
  gap: 22px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding: 4px 4px 28px;
  margin: 0 -4px;
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.text}55 transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.text}55;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const Card = styled(motion(Link))`
  position: relative;
  flex: 0 0 320px;
  height: 440px;
  border-radius: 22px;
  overflow: hidden;
  scroll-snap-align: start;
  text-decoration: none;
  color: #fff;
  background: #1a1a1a;
  box-shadow: 0 20px 50px -30px rgba(15, 17, 21, 0.7);
  transition: transform 0.4s ease, box-shadow 0.4s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 30px 70px -32px ${(props) => props.theme.text};
  }

  &:hover img {
    transform: scale(1.08);
  }

  @media screen and (max-width: 520px) {
    flex: 0 0 78%;
    height: 400px;
  }
`;

export const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
`;

export const CardOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 35%,
    rgba(0, 0, 0, 0.35) 65%,
    rgba(0, 0, 0, 0.85) 100%
  );
`;

export const CardTopRow = styled.div`
  position: absolute;
  top: 18px;
  left: 18px;
  right: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  z-index: 2;
`;

export const FlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #0f1115;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  backdrop-filter: blur(6px);
`;

export const DateBadge = styled.span`
  font-size: 11px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  /* date ranges must stay on one line — wrapping collides with the flag badge */
  white-space: nowrap;
  flex-shrink: 0;
  /* the badge sits directly on the photo, which can be bright */
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
`;

export const CardBody = styled.div`
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 22px;
  z-index: 2;
`;

export const CardTitle = styled.h3`
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 6px;
  font-family: 'Poppins', sans-serif;
  line-height: 1.15;
`;

export const CardMeta = styled.p`
  font-size: 13px;
  margin: 0 0 16px;
  color: rgba(255, 255, 255, 0.78);
`;

export const CardCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.5);
  padding-bottom: 3px;
  width: max-content;
  transition: border-color 0.25s ease;

  ${Card}:hover & {
    border-color: ${(props) => props.theme.text};
  }
`;
